/**
 * ProCalculator - Professional Tariff Calculator
 *
 * Main orchestrator component that manages state, effects, and business logic
 * Composes Header, Config, Monthly Parameters, and Results components
 *
 * Refactored from monolithic ProCalculator.tsx (1,590 lines) into modular structure
 */

import { useEffect, useRef } from 'react';
import { Container, Stack, Paper, Alert, Group, Button } from '@mantine/core';
import { IconInfoCircle, IconWand, IconAlertCircle } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import { notifications } from '@mantine/notifications';
import { Layout } from '../../shared/components';
import { MonthlyParametersListView, DataSourcesAvailability, YearMismatchWarning } from './components/ProCalculator';
import { useSEO } from '../../shared/hooks';
import { useAuth } from '../auth';
import { useFeatureToggle } from '../../shared/contexts';
import { ProCalculatorDisabled } from '../../shared/components/FeatureDisabledPage';
import {
  validateProCalculatorForm,
  hasValidationErrors,
  formatValidationErrors,
} from '../../utils/validation';
import { useGetHourlyData } from '../../shared/hooks';
import { useYearDataAvailability } from '../../shared/hooks';
import type { ProCalculatorFormValues, ProCalculatorResults, TariffType } from '../../types/proCalculator';
import {
  generateParametersForAllMonths,
  calculateProfessionalTariffs,
  prepareCalculationInput,
} from '../../utils/proCalculator';
import {
  getSavedProCalculationByYear,
  parseSavedProCalculation,
} from '../../lib/api/savedProCalculationApi';
import { ProCalculatorHeader } from './components/ProCalculatorHeader';
import { ProCalculatorConfig } from './components/ProCalculatorConfig';
import { ProCalculatorResults as ProCalculatorResultsSection } from './components/ProCalculatorResults';
import { useProCalculatorState } from './hooks/useProCalculatorState';
import { useProCalculatorMutations } from './hooks/useProCalculatorMutations';
import { useHasDynamicPriceData } from '../g14-calculator/hooks/useHasDynamicPriceData';
import { MONTH_NAMES, EXPECTED_TARIFF_TYPES } from './constants/proCalculator.constants';
import { BUTTON_LABELS } from '../../constants/uiLabels';

/**
 * Professional Tariff Calculator Page
 *
 * Allows users to enter exact invoice amounts and compare simulations for other tariffs.
 * MVP features:
 * - Monthly tariff selection
 * - Monthly consumption input
 * - Hourly pattern selection per month
 * - 4-value system (a: from tariff, b: empty, c: user override, d: final)
 * - Inline editing of (c) values
 * - Comparison table for all 6 tariffs
 *
 * Future features:
 * - PDF invoice import (b values)
 * - Charts and visualizations
 * - CSV export
 *
 * @see /requirements/professionalTariffCalculator.txt - Complete requirements
 */
export function ProCalculator() {
  // Feature toggle check - must be first hook call
  const isProCalculatorEnabled = useFeatureToggle('pro_calculator_enabled', true);

  // Early return if feature is disabled
  // All other hooks are in ProCalculatorContent to comply with React Rules of Hooks
  if (!isProCalculatorEnabled) {
    return <ProCalculatorDisabled />;
  }

  return <ProCalculatorContent />;
}

/**
 * Inner component containing all ProCalculator logic
 * Separated to ensure all hooks are called unconditionally within this component
 */
function ProCalculatorContent() {
  useSEO({ title: 'Kalkulator Profesjonalny', noindex: true });

  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // State management with useReducer
  const [state, dispatch] = useProCalculatorState();
  const { saveCalculation } = useProCalculatorMutations();

  // Hook to fetch Tauron hourly data
  const { getHourlyData } = useGetHourlyData();

  // Hook to check data availability across years (for year selector badges)
  const {
    yearStatuses,
    suggestedYear,
    suggestedYearReason,
    isLoading: yearDataLoading,
  } = useYearDataAvailability();

  // Hook to check if G14 dynamic price data exists for current year
  const { hasData: g14HasData } = useHasDynamicPriceData(state.formValues.calculationYear);

  // UX-004: Compute years with hourly data for YearMismatchWarning
  const yearsWithHourlyData = Array.from(yearStatuses.entries())
    .filter(([_, status]) => status.hasHourlyData)
    .map(([year]) => year);

  // Ref to track if data has been loaded (prevents double-loading in React StrictMode)
  const hasLoadedData = useRef(false);

  // Ref to track if we've already auto-reset patterns (prevents infinite loops)
  const hasAutoResetPatterns = useRef(false);

  /**
   * Load saved professional calculation on mount (if authenticated)
   * Auto-loads calculation data for current year to provide seamless UX
   */
  useEffect(() => {
    const loadSavedProCalculation = async () => {
      // Only load if user is authenticated
      if (!user) return;

      // Prevent double-loading (React StrictMode runs useEffect twice in dev)
      if (hasLoadedData.current) return;

      try {
        const currentUser = await getCurrentUser();
        const currentYear = new Date().getFullYear();

        // Attempt to load saved professional calculation for current year
        const savedProCalc = await getSavedProCalculationByYear(currentUser.userId, currentYear);

        if (savedProCalc) {
          // Parse saved calculation back to form values only (NOT results)
          const { formValues: loadedFormValues } = parseSavedProCalculation(savedProCalc);

          // Set form values (this will populate the monthly parameters table)
          dispatch({ type: 'SET_FORM_VALUES', payload: loadedFormValues });

          // Mark calculation as existing (for save/update UX)
          dispatch({ type: 'SET_CALCULATION_EXISTS', payload: true });

          // IMPORTANT: Do NOT load results from saved calculation
          // Results will always be recalculated when user clicks "Oblicz"
          // This ensures results are always fresh and based on latest calculation logic
        }
      } catch (err) {
        // Development only logging
        if (import.meta.env.DEV) {
          console.error('[ProCalculator] loadSavedProCalculation error:', err);
        }
        // Don't show error to user - this is optional data
      } finally {
        // Mark data as loaded (prevents double-loading in React StrictMode)
        hasLoadedData.current = true;
      }
    };

    void loadSavedProCalculation();
  }, [user, dispatch]);

  /**
   * Auto-reset invalid hourly patterns
   *
   * If user has FROM_CSV_IMPORT patterns but no corresponding data,
   * automatically reset those months to UNIFORM pattern.
   *
   * This prevents "stuck" state where user has invalid patterns from:
   * - Previous test sessions
   * - Cached form data
   * - Expired imported data
   *
   * Runs when:
   * - Component mounts (checks loaded data)
   * - hourlyPatternFromCSV changes (import/clear)
   */
  useEffect(() => {
    // Skip if we've already done auto-reset in this session
    if (hasAutoResetPatterns.current) return;

    // Check if any months have invalid patterns
    const monthsWithInvalidPatterns = state.formValues.months
      .map((month, index) => ({ month, index }))
      .filter(({ month, index }) => {
        // FROM_CSV_IMPORT without data
        if (month.hourlyPattern === 'FROM_CSV_IMPORT' && (!state.hourlyPatternFromCSV || !state.hourlyPatternFromCSV[index])) {
          return true;
        }
        return false;
      });

    // If found invalid patterns, auto-reset them to UNIFORM
    if (monthsWithInvalidPatterns.length > 0) {
      const affectedMonthNames = monthsWithInvalidPatterns
        .map(({ index }) => MONTH_NAMES[index])
        .join(', ');

      // Update form values - reset invalid patterns to UNIFORM
      const updatedMonths = state.formValues.months.map((month, index) => {
        const isInvalid = monthsWithInvalidPatterns.some(({ index: invalidIndex }) => invalidIndex === index);
        if (isInvalid) {
          return {
            ...month,
            hourlyPattern: 'UNIFORM' as const,
          };
        }
        return month;
      });

      dispatch({
        type: 'SET_FORM_VALUES',
        payload: {
          ...state.formValues,
          months: updatedMonths as typeof state.formValues.months,
        },
      });

      // Show notification to user
      notifications.show({
        title: 'Automatyczna korekta wzorców',
        message: `Miesiące: ${affectedMonthNames} zostały zmienione na wzorzec "Równomierne", ponieważ brakuje wymaganych danych importowanych.`,
        color: 'blue',
        autoClose: 8000,
      });

      // Mark that we've done auto-reset
      hasAutoResetPatterns.current = true;
    }
  }, [state.hourlyPatternFromCSV, state.formValues, dispatch]);

  /**
   * Check if user has any hourly data (CSV imports)
   * Used to determine button text and behavior
   *
   * IMPORTANT: Uses formValues.calculationYear to check for data in the correct year.
   * This fixes the bug where frontend was checking current calendar year (2026)
   * but user's data might be from a different year (e.g., 2025).
   */
  useEffect(() => {
    const checkHourlyData = async () => {
      if (!user) {
        dispatch({ type: 'SET_HAS_HOURLY_DATA', payload: false });
        return;
      }

      try {
        // Check if user has ANY hourly data for the SELECTED calculation year
        // We'll check first month - if it exists, user likely has data
        const yearMonth = `${state.formValues.calculationYear}-01`; // January of selected year

        const result = await getHourlyData(yearMonth);

        // If we got data, user has hourly data available
        if (result && result.hourlyData && result.hourlyData.length > 0) {
          dispatch({ type: 'SET_HAS_HOURLY_DATA', payload: true });
        } else {
          dispatch({ type: 'SET_HAS_HOURLY_DATA', payload: false });
        }
      } catch (error) {
        // If error (e.g., no data found), user doesn't have hourly data
        dispatch({ type: 'SET_HAS_HOURLY_DATA', payload: false });
      }
    };

    void checkHourlyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, state.formValues.calculationYear]); // Re-check when user or calculation year changes

  // Sync G14 price data availability from hook to state
  useEffect(() => {
    dispatch({ type: 'SET_HAS_G14_PRICE_DATA', payload: g14HasData });
  }, [g14HasData, dispatch]);


  /**
   * Handle form values update
   * Called when user edits monthly configuration (tariff, consumption, pattern)
   */
  const handleFormValuesChange = (newValues: ProCalculatorFormValues) => {
    dispatch({ type: 'SET_FORM_VALUES', payload: newValues });
  };

  /**
   * Handle generate parameters button click
   * If user has hourly data: Fetches meter data, populates consumption and patterns
   * If user has NO hourly data: Redirects to /hourly-import page
   */
  const handleGenerateParameters = async () => {
    // Check if user has hourly data
    if (state.hasHourlyData === false) {
      // User has NO hourly data - redirect to import page WITHOUT clearing calculator
      notifications.show({
        title: 'Brak danych z licznika',
        message: 'Aby skorzystać z tej funkcji, najpierw prześlij raport z licznika zdalnego odczytu.',
        color: 'blue',
        icon: <IconAlertCircle size={20} />,
        autoClose: 5000,
      });

      // Redirect to hourly import page
      navigate('/konto/import');
      return; // Stop here - DO NOT execute parameter generation
    }

    // User HAS hourly data - fetch meter data and populate calculator
    dispatch({ type: 'SET_GENERATING_PARAMS', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      // IMPORTANT: Use formValues.calculationYear instead of new Date().getFullYear()
      // This ensures we fetch data for the year the user selected (e.g., 2025)
      // not the current calendar year (e.g., 2026)
      const calculationYear = state.formValues.calculationYear;
      const csvPatterns: Record<number, { weekday: number[]; weekend: number[] }> = {};
      const monthlyConsumptions: number[] = [];
      const monthlyTariffs: string[] = [];

      // Fetch hourly data for all 12 months in parallel
      const hourlyDataPromises = Array.from({ length: 12 }, async (_, monthIndex) => {
        const yearMonth = `${calculationYear}-${String(monthIndex + 1).padStart(2, '0')}`;

        try {
          const result = await getHourlyData(yearMonth);

          if (result && result.hourlyData && result.hourlyData.length > 0) {
            // Convert hourly data to pattern
            const pattern = convertHourlyDataToPattern(result.hourlyData, calculationYear, monthIndex + 1);
            csvPatterns[monthIndex] = pattern;

            // Extract total consumption for the month (round to avoid floating point precision)
            const totalConsumption = result.totalConsumption || result.hourlyData.reduce((sum, h) => sum + h.consumption, 0);
            monthlyConsumptions[monthIndex] = Math.round(totalConsumption);

            // Extract tariff type if available
            monthlyTariffs[monthIndex] = result.tariffType || state.formValues.months[monthIndex].tariff;

            return { monthIndex, success: true, consumption: totalConsumption };
          } else {
            // No data for this month - use existing values
            monthlyConsumptions[monthIndex] = state.formValues.months[monthIndex].consumption;
            monthlyTariffs[monthIndex] = state.formValues.months[monthIndex].tariff;
            return { monthIndex, success: false };
          }
        } catch (error) {
          // Error fetching data - use existing values
          monthlyConsumptions[monthIndex] = state.formValues.months[monthIndex].consumption;
          monthlyTariffs[monthIndex] = state.formValues.months[monthIndex].tariff;
          return { monthIndex, success: false, error };
        }
      });

      const results = await Promise.all(hourlyDataPromises);

      // Count successful imports
      const successCount = results.filter(r => r.success).length;

      if (successCount === 0) {
        // No data found for any month
        notifications.show({
          title: 'Brak danych',
          message: 'Nie znaleziono danych z licznika dla żadnego miesiąca. Prześlij raporty CSV.',
          color: 'orange',
          icon: <IconAlertCircle size={20} />,
          autoClose: 5000,
        });
        navigate('/konto/import');
        return;
      }

      // Save patterns to state
      dispatch({ type: 'SET_HOURLY_PATTERN_FROM_CSV', payload: csvPatterns });

      // Prepare input for generateParametersForAllMonths
      const monthsInput = state.formValues.months.map((_month, index) => ({
        tariff: monthlyTariffs[index] as TariffType,
        consumption: monthlyConsumptions[index],
        monthIndex: index,
      }));

      // Call backend API to generate parameters
      const generatedParams = await generateParametersForAllMonths(monthsInput);

      // Update form values with generated parameters and fetched meter data
      // CRITICAL: Use consumption from meter data, set hourlyPattern to FROM_CSV_IMPORT where data exists
      const updatedMonths = state.formValues.months.map((month, index) => {
        const oldParams = month.parameters;
        const newParams = generatedParams[index];
        const hasDataForMonth = csvPatterns[index] !== undefined;

        return {
          tariff: monthlyTariffs[index] as TariffType,
          consumption: monthlyConsumptions[index],
          hourlyPattern: hasDataForMonth ? ('FROM_CSV_IMPORT' as const) : month.hourlyPattern,
          parameters: {
            energyElectricCost: {
              valueFromBasic: newParams.energyElectricCost,
              valueFromInvoice: null, // Clear invoice values when importing from meter
              valueOverridden: oldParams.energyElectricCost.valueOverridden,
              valueFinal: oldParams.energyElectricCost.valueOverridden ?? newParams.energyElectricCost,
            },
            handlingFee: {
              valueFromBasic: newParams.handlingFee,
              valueFromInvoice: null,
              valueOverridden: oldParams.handlingFee.valueOverridden,
              valueFinal: oldParams.handlingFee.valueOverridden ?? newParams.handlingFee,
            },
            networkFixedFee: {
              valueFromBasic: newParams.networkFixedFee,
              valueFromInvoice: null,
              valueOverridden: oldParams.networkFixedFee.valueOverridden,
              valueFinal: oldParams.networkFixedFee.valueOverridden ?? newParams.networkFixedFee,
            },
            transitionFee: {
              valueFromBasic: newParams.transitionFee,
              valueFromInvoice: null,
              valueOverridden: oldParams.transitionFee.valueOverridden,
              valueFinal: oldParams.transitionFee.valueOverridden ?? newParams.transitionFee,
            },
            qualityFee: {
              valueFromBasic: newParams.qualityFee,
              valueFromInvoice: null,
              valueOverridden: oldParams.qualityFee.valueOverridden,
              valueFinal: oldParams.qualityFee.valueOverridden ?? newParams.qualityFee,
            },
            networkVariableFee: {
              valueFromBasic: newParams.networkVariableFee,
              valueFromInvoice: null,
              valueOverridden: oldParams.networkVariableFee.valueOverridden,
              valueFinal: oldParams.networkVariableFee.valueOverridden ?? newParams.networkVariableFee,
            },
            ozeFee: {
              valueFromBasic: newParams.ozeFee,
              valueFromInvoice: null,
              valueOverridden: oldParams.ozeFee.valueOverridden,
              valueFinal: oldParams.ozeFee.valueOverridden ?? newParams.ozeFee,
            },
            cogenerationFee: {
              valueFromBasic: newParams.cogenerationFee,
              valueFromInvoice: null,
              valueOverridden: oldParams.cogenerationFee.valueOverridden,
              valueFinal: oldParams.cogenerationFee.valueOverridden ?? newParams.cogenerationFee,
            },
            subscriptionFee: {
              valueFromBasic: newParams.subscriptionFee,
              valueFromInvoice: null,
              valueOverridden: oldParams.subscriptionFee.valueOverridden,
              valueFinal: oldParams.subscriptionFee.valueOverridden ?? newParams.subscriptionFee,
            },
            powerFee: {
              valueFromBasic: newParams.powerFee,
              valueFromInvoice: null,
              valueOverridden: oldParams.powerFee.valueOverridden,
              valueFinal: oldParams.powerFee.valueOverridden ?? newParams.powerFee,
            },
          },
        };
      });

      dispatch({
        type: 'SET_FORM_VALUES',
        payload: {
          ...state.formValues,
          months: updatedMonths as ProCalculatorFormValues['months'],
        },
      });

      // Show success notification
      notifications.show({
        title: 'Dane załadowane',
        message: `Pomyślnie załadowano dane z licznika dla ${successCount} z 12 miesięcy.`,
        color: 'green',
        autoClose: 5000,
      });
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: 'Wystąpił błąd podczas generowania parametrów. Spróbuj ponownie.' });
      if (import.meta.env.DEV) {
        console.error('[ProCalculator] handleGenerateParameters error:', err);
      }
    } finally {
      dispatch({ type: 'SET_GENERATING_PARAMS', payload: false });
    }
  };

  /**
   * Convert hourly data to weekday/weekend pattern (percentages)
   *
   * Groups hourly consumption by weekday/weekend and converts to percentage distribution.
   * Used when loading "Z raportu Tauron" pattern for Advanced Calculator.
   *
   * @param hourlyData - Array of hourly consumption points
   * @param year - Year for date calculations
   * @param month - Month (1-12)
   * @returns Pattern with weekday and weekend percentage arrays (24 values each, sum = 100%)
   */
  const convertHourlyDataToPattern = (
    hourlyData: Array<{
      hour: number;
      timestamp: string;
      consumption: number;
      isImported: boolean;
      completeness: number;
    }>,
    _year: number,
    _month: number
  ): { weekday: number[]; weekend: number[] } => {
    // Initialize arrays for 24 hours
    const weekdayHours = new Array(24).fill(0);
    const weekendHours = new Array(24).fill(0);
    const weekdayCounts = new Array(24).fill(0);
    const weekendCounts = new Array(24).fill(0);

    // Group hours by day type (weekday vs weekend)
    hourlyData.forEach((point) => {
      const date = new Date(point.timestamp);
      const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
      const hour = date.getHours();
      const consumption = point.consumption;

      if (dayOfWeek === 0 || dayOfWeek === 6) {
        // Weekend (Saturday or Sunday)
        weekendHours[hour] += consumption;
        weekendCounts[hour] += 1;
      } else {
        // Weekday (Monday-Friday)
        weekdayHours[hour] += consumption;
        weekdayCounts[hour] += 1;
      }
    });

    // Calculate average consumption per hour for each day type
    const avgWeekdayHours = weekdayHours.map((total, hour) =>
      weekdayCounts[hour] > 0 ? total / weekdayCounts[hour] : 0
    );
    const avgWeekendHours = weekendHours.map((total, hour) =>
      weekendCounts[hour] > 0 ? total / weekendCounts[hour] : 0
    );

    // Convert to percentages (normalize so sum = 100%)
    const weekdaySum = avgWeekdayHours.reduce((sum, val) => sum + val, 0);
    const weekendSum = avgWeekendHours.reduce((sum, val) => sum + val, 0);

    const weekdayPercentages =
      weekdaySum > 0
        ? avgWeekdayHours.map((val) => (val / weekdaySum) * 100)
        : Array(24).fill(100 / 24); // Fallback to uniform if no data

    const weekendPercentages =
      weekendSum > 0
        ? avgWeekendHours.map((val) => (val / weekendSum) * 100)
        : Array(24).fill(100 / 24); // Fallback to uniform if no data

    return {
      weekday: weekdayPercentages,
      weekend: weekendPercentages,
    };
  };

  /**
   * Perform calculation (core logic extracted for reuse)
   * Can be called from button click or automatic recalculation
   */
  const performCalculation = async (formData: ProCalculatorFormValues, silent = false) => {
    if (!silent) {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
    }

    try {
      // Step 0: CLIENT-SIDE VALIDATION (prevent invalid data from reaching backend)
      const validationErrors = validateProCalculatorForm(formData);

      if (hasValidationErrors(validationErrors)) {
        const errorMessage = formatValidationErrors(validationErrors);

        if (!silent) {
          notifications.show({
            title: 'Błędy walidacji',
            message: `Proszę poprawić następujące błędy:\n\n${errorMessage}`,
            color: 'red',
            autoClose: 10000,
          });
          dispatch({ type: 'SET_ERROR', payload: `Błędy walidacji: ${errorMessage}` });
        }

        return; // Stop execution - do not call backend
      }

      // Step 1: Fetch CSV data for months with FROM_CSV_IMPORT pattern
      let csvPatterns: Record<number, { weekday: number[]; weekend: number[] }> | undefined;

      // Find months that use FROM_CSV_IMPORT pattern
      const monthsWithCSVPattern = formData.months
        .map((month, index) => ({ month, index }))
        .filter(({ month }) => month.hourlyPattern === 'FROM_CSV_IMPORT');

      if (monthsWithCSVPattern.length > 0) {
        csvPatterns = {};

        // Fetch data for each month in parallel
        try {
          await Promise.all(
            monthsWithCSVPattern.map(async ({ index }) => {
              const yearMonth = `${formData.calculationYear}-${String(index + 1).padStart(2, '0')}`;

              try {
                const result = await getHourlyData(yearMonth);

                // Convert hourly data to weekday/weekend pattern
                const pattern = convertHourlyDataToPattern(result.hourlyData, formData.calculationYear, index + 1);

                if (csvPatterns) {
                  csvPatterns[index] = pattern;
                }

              } catch (error) {
                // Pattern will be missing - backend will fallback to UNIFORM
              }
            })
          );

          // If we successfully loaded some patterns, save them to state
          if (Object.keys(csvPatterns).length > 0) {
            dispatch({ type: 'SET_HOURLY_PATTERN_FROM_CSV', payload: csvPatterns });
          }
        } catch (error) {
          // Continue with calculation - backend will handle missing patterns with fallback
        }

        // Step 1.5: Validate that all FROM_CSV_IMPORT months have data
        const monthsWithMissingCSVData = monthsWithCSVPattern.filter(
          ({ index }) => !csvPatterns || !csvPatterns[index]
        );

        if (monthsWithMissingCSVData.length > 0) {
          const affectedMonths = monthsWithMissingCSVData.map(({ index }) => MONTH_NAMES[index]).join(', ');

          if (!silent) {
            notifications.show({
              title: 'Brak danych CSV',
              message: `Miesiące: ${affectedMonths} używają wzorca "Z importu CSV", ale nie udało się pobrać danych godzinowych.\n\nProszę:\n1. Sprawdzić czy masz zaimportowane dane CSV dla tych miesięcy, lub\n2. Zmienić wzorzec godzinowy na inny (np. Równomiernie)`,
              color: 'red',
              autoClose: 15000,
            });
            dispatch({ type: 'SET_ERROR', payload: 'Wzorzec "Z importu CSV" wymaga zaimportowanych danych godzinowych' });
          }

          return; // Stop execution - prevent Lambda error
        }
      }

      // Prepare input for backend API
      const input = prepareCalculationInput(formData.months, undefined, csvPatterns);

      // Call backend API to calculate tariff simulations
      const backendResults = await calculateProfessionalTariffs(input);

      // Handle potential double-JSON encoding from backend
      // AppSync may return JSON string instead of parsed object
      let parsedResults = backendResults;
      if (typeof backendResults === 'string') {
        try {
          parsedResults = JSON.parse(backendResults);
        } catch (parseError) {
          throw new Error('Invalid JSON response from backend');
        }
      }

      // Validate backend response structure
      if (!parsedResults || !parsedResults.simulations) {
        throw new Error('Invalid response from backend: missing simulations data');
      }

      // Validate that all expected tariffs are present
      // G14 is optional - only required if user enabled it and has price data
      for (const tariff of EXPECTED_TARIFF_TYPES) {
        if (!parsedResults.simulations[tariff]) {
          throw new Error(`Invalid response from backend: missing ${tariff} simulation`);
        }
      }

      // Validate G14 if user enabled it
      if (state.includeG14 && state.hasG14PriceData && !parsedResults.simulations.G14) {
        throw new Error('Invalid response from backend: missing G14 simulation (expected when G14 is enabled)');
      }

      // Transform backend response to frontend ProCalculatorResults format
      const results: ProCalculatorResults = {
        input: formData,
        simulations: {
          G11: {
            tariffCode: 'G11',
            tariffName: 'G11 - całodobowa',
            annualCost: parsedResults.simulations.G11.annualCost,
            monthlyCost: parsedResults.simulations.G11.monthlyCost,
            savings: parsedResults.simulations.G11.savings,
            savingsPercent: (parsedResults.simulations.G11.savings / parsedResults.simulations.G11.annualCost) * 100,
            isBaseline: state.baselineTariff === 'G11',
            isRecommended: parsedResults.recommendedTariff === 'G11',
            monthlyBreakdown: parsedResults.simulations.G11.monthlyBreakdown,
          },
          G12: {
            tariffCode: 'G12',
            tariffName: 'G12 - dzień/noc',
            annualCost: parsedResults.simulations.G12.annualCost,
            monthlyCost: parsedResults.simulations.G12.monthlyCost,
            savings: parsedResults.simulations.G12.savings,
            savingsPercent: (parsedResults.simulations.G12.savings / parsedResults.simulations.G12.annualCost) * 100,
            isBaseline: state.baselineTariff === 'G12',
            isRecommended: parsedResults.recommendedTariff === 'G12',
            monthlyBreakdown: parsedResults.simulations.G12.monthlyBreakdown,
          },
          G12w: {
            tariffCode: 'G12w',
            tariffName: 'G12w - weekend',
            annualCost: parsedResults.simulations.G12w.annualCost,
            monthlyCost: parsedResults.simulations.G12w.monthlyCost,
            savings: parsedResults.simulations.G12w.savings,
            savingsPercent: (parsedResults.simulations.G12w.savings / parsedResults.simulations.G12w.annualCost) * 100,
            isBaseline: state.baselineTariff === 'G12w',
            isRecommended: parsedResults.recommendedTariff === 'G12w',
            monthlyBreakdown: parsedResults.simulations.G12w.monthlyBreakdown,
          },
          G12as: {
            tariffCode: 'G12as',
            tariffName: 'G12as - anti-smog',
            annualCost: parsedResults.simulations.G12as.annualCost,
            monthlyCost: parsedResults.simulations.G12as.monthlyCost,
            savings: parsedResults.simulations.G12as.savings,
            savingsPercent: (parsedResults.simulations.G12as.savings / parsedResults.simulations.G12as.annualCost) * 100,
            isBaseline: state.baselineTariff === 'G12as',
            isRecommended: parsedResults.recommendedTariff === 'G12as',
            monthlyBreakdown: parsedResults.simulations.G12as.monthlyBreakdown,
          },
          G13: {
            tariffCode: 'G13',
            tariffName: 'G13 - trzy strefy',
            annualCost: parsedResults.simulations.G13.annualCost,
            monthlyCost: parsedResults.simulations.G13.monthlyCost,
            savings: parsedResults.simulations.G13.savings,
            savingsPercent: (parsedResults.simulations.G13.savings / parsedResults.simulations.G13.annualCost) * 100,
            isBaseline: state.baselineTariff === 'G13',
            isRecommended: parsedResults.recommendedTariff === 'G13',
            monthlyBreakdown: parsedResults.simulations.G13.monthlyBreakdown,
          },
          G13s: {
            tariffCode: 'G13s',
            tariffName: 'G13s - dynamiczna OZE',
            annualCost: parsedResults.simulations.G13s.annualCost,
            monthlyCost: parsedResults.simulations.G13s.monthlyCost,
            savings: parsedResults.simulations.G13s.savings,
            savingsPercent: (parsedResults.simulations.G13s.savings / parsedResults.simulations.G13s.annualCost) * 100,
            isBaseline: state.baselineTariff === 'G13s',
            isRecommended: parsedResults.recommendedTariff === 'G13s',
            monthlyBreakdown: parsedResults.simulations.G13s.monthlyBreakdown,
          },
          // G14 is optional - only include if user enabled it and has price data
          ...(state.includeG14 && state.hasG14PriceData && parsedResults.simulations.G14 ? {
            G14: {
              tariffCode: 'G14' as const,
              tariffName: 'G14 - dynamiczna RCE',
              annualCost: parsedResults.simulations.G14.annualCost,
              monthlyCost: parsedResults.simulations.G14.monthlyCost,
              savings: parsedResults.simulations.G14.savings,
              savingsPercent: (parsedResults.simulations.G14.savings / parsedResults.simulations.G14.annualCost) * 100,
              isBaseline: state.baselineTariff === 'G14',
              isRecommended: parsedResults.recommendedTariff === 'G14',
              monthlyBreakdown: parsedResults.simulations.G14.monthlyBreakdown,
              rcePriceStats: parsedResults.simulations.G14.rcePriceStats,
            }
          } : {}),
        },
        recommendedTariff: parsedResults.recommendedTariff,
        potentialSavings: parsedResults.potentialSavings,
        savingsPercent: (parsedResults.potentialSavings / ((parsedResults.simulations as unknown as Record<string, { annualCost: number } | undefined>)[parsedResults.recommendedTariff]?.annualCost || 1)) * 100,
        calculatedAt: new Date(),
      };

      dispatch({ type: 'SET_RESULTS', payload: results });
      dispatch({ type: 'SET_BASELINE_TARIFF', payload: formData.months[0].tariff }); // Set baseline to first month's tariff

      return results;
    } catch (err) {
      if (!silent) {
        dispatch({ type: 'SET_ERROR', payload: 'Wystąpił błąd podczas obliczania. Spróbuj ponownie.' });
      }
      if (import.meta.env.DEV) {
        console.error('[ProCalculator] performCalculation error:', err);
      }
      throw err;
    } finally {
      if (!silent) {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  };

  /**
   * Handle calculate button click
   * Wrapper around performCalculation with UI feedback
   */
  const handleCalculate = async () => {
    try {
      await performCalculation(state.formValues, false);
    } catch (err) {
      // Error already handled in performCalculation
    }
  };

  /**
   * Handle baseline tariff change
   * Recalculates savings/loss based on new baseline
   */
  const handleBaselineTariffChange = (tariff: TariffType) => {
    dispatch({ type: 'SET_BASELINE_TARIFF', payload: tariff });
  };

  /**
   * Handle save professional calculation
   * Saves calculation data to backend via GraphQL API
   */
  const handleSaveProCalculation = async () => {
    dispatch({ type: 'SET_IS_SAVING', payload: true });
    dispatch({ type: 'SET_SAVE_ERROR', payload: undefined });
    dispatch({ type: 'SET_SAVE_SUCCESS', payload: false });

    try {
      // Validate that we have form values and results
      if (!state.formValues || !state.results) {
        throw new Error('Brak danych do zapisania');
      }

      // Save to backend (creates or updates for current year)
      await saveCalculation(state.formValues, state.results);

      // Update calculation exists flag
      dispatch({ type: 'SET_CALCULATION_EXISTS', payload: true });

      // Show success message
      dispatch({ type: 'SET_SAVE_SUCCESS', payload: true });

      // Clear success message after 5 seconds
      setTimeout(() => {
        dispatch({ type: 'SET_SAVE_SUCCESS', payload: false });
      }, 5000);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Nie udało się zapisać kalkulacji. Spróbuj ponownie.';
      dispatch({ type: 'SET_SAVE_ERROR', payload: errorMessage });

      // Development only logging
      if (import.meta.env.DEV) {
        console.error('[ProCalculator] handleSaveProCalculation error:', err);
      }
    } finally {
      dispatch({ type: 'SET_IS_SAVING', payload: false });
    }
  };

  /**
   * Handle results clear (when year or handling fee changes)
   */
  const handleResultsClear = () => {
    dispatch({ type: 'SET_RESULTS', payload: null });
  };

  return (
    <Layout isAuthenticated={user !== null}>
      <Container size="xl" py={40} px={{ base: 'md', sm: 'lg' }}>
        <Stack gap="xl">
          {/* Page Header */}
          <ProCalculatorHeader
            isAuthenticated={user !== null}
            showInfoAlert={!authLoading && user !== null}
          />

          {/* Config Panel - Only show for authenticated users */}
          {user && (
            <ProCalculatorConfig
              formValues={state.formValues}
              yearStatuses={yearStatuses}
              suggestedYear={suggestedYear}
              suggestedYearReason={suggestedYearReason}
              yearDataLoading={yearDataLoading}
              hasG14PriceData={state.hasG14PriceData}
              includeG14={state.includeG14}
              onFormValuesChange={handleFormValuesChange}
              onIncludeG14Change={(include) => dispatch({ type: 'SET_INCLUDE_G14', payload: include })}
              onResultsClear={handleResultsClear}
            />
          )}

          {/* UX-004: Year Mismatch Warning */}
          {user && !yearDataLoading && yearsWithHourlyData.length > 0 && (
            <YearMismatchWarning
              selectedYear={state.formValues.calculationYear}
              availableYears={yearsWithHourlyData}
              onYearChange={(year) => handleFormValuesChange({ ...state.formValues, calculationYear: year })}
            />
          )}

          {/* Monthly Parameters Table - Only show for authenticated users */}
          {user && (
            <Paper p="xl" shadow="sm" withBorder>
              <Group justify="space-between" align="center" mb="lg">
                <div>
                  <h2 style={{ margin: 0 }}>Dane miesięczne</h2>
                </div>
                <Button
                  variant="light"
                  leftSection={<IconWand size={18} />}
                  onClick={handleGenerateParameters}
                  loading={state.generatingParams}
                  disabled={state.loading || state.hasHourlyData === null}
                >
                  {state.hasHourlyData === false
                    ? BUTTON_LABELS.GO_TO_CSV_IMPORT
                    : BUTTON_LABELS.GENERATE_FROM_CSV_IMPORT
                  }
                </Button>
              </Group>

              {/* Data Sources Availability - UX-003: Show what data is available */}
              <DataSourcesAvailability
                hasHourlyData={state.hasHourlyData === true}
                hourlyDataMonths={state.hourlyPatternFromCSV ? Object.keys(state.hourlyPatternFromCSV).length : 0}
                year={state.formValues.calculationYear}
                loading={state.loading}
              />

              <MonthlyParametersListView
                formValues={state.formValues}
                onChange={handleFormValuesChange}
                csvPatterns={state.hourlyPatternFromCSV}
              />

              {/* Calculate Button */}
              <Group justify="flex-end" mt="xl">
                <Button
                  size="lg"
                  onClick={handleCalculate}
                  loading={state.loading}
                  disabled={state.generatingParams}
                >
                  {BUTTON_LABELS.CALCULATE_COMPARISON}
                </Button>
              </Group>
            </Paper>
          )}

          {/* Error Alert */}
          {state.error && (
            <Alert
              icon={<IconInfoCircle size={20} />}
              title="Błąd"
              color="red"
              variant="filled"
              onClose={() => dispatch({ type: 'SET_ERROR', payload: null })}
              withCloseButton
            >
              {state.error}
            </Alert>
          )}

          {/* Results Section - Only show when we have results */}
          {state.results && !state.loading && (
            <ProCalculatorResultsSection
              results={state.results}
              formValues={state.formValues}
              baselineTariff={state.baselineTariff}
              isSaving={state.isSaving}
              saveError={state.saveError}
              saveSuccess={state.saveSuccess}
              calculationExists={state.calculationExists}
              loading={state.loading}
              onBaselineTariffChange={handleBaselineTariffChange}
              onSaveProCalculation={handleSaveProCalculation}
            />
          )}
        </Stack>
      </Container>
    </Layout>
  );
}
